/**
 * AuthContext — React context for the Explorer's authentication state.
 *
 * Mirrors survey-designer/src/context/AuthContext.js and (originally)
 * console/src/context/AuthContext.js. Trust model is identical across
 * all three apps: the Hanko session cookie (scoped to .samsurveyor.com)
 * rides on a credentialed fetch to dbam's /dbam/v1/me, dbam verifies the
 * JWT against Hanko's JWKS, and returns the sam-dbam user profile.
 *
 * Notes specific to Explorer:
 *  - Explorer reaches sam-dbam through its own Express proxy at /api/*,
 *    BUT the /me check goes directly to the dbam custom domain so the
 *    cookie rides natively. Both hosts are under *.samsurveyor.com,
 *    so the cross-origin fetch is still same-site for the cookie.
 *  - Until server.js learns to forward cookies upstream, the existing
 *    /api/* proxy continues to pass identity via X-User-ID / X-Org-ID
 *    headers — but now those headers are populated from this authenticated
 *    user object, not from URL query parameters.
 *
 * Usage:
 *   import { AuthProvider, useAuth } from './contexts/AuthContext';
 *
 *   // index.js:
 *   <AuthProvider><ProtectedRoute><App /></ProtectedRoute></AuthProvider>
 *
 *   // any component:
 *   const { user, loading, logout } = useAuth();
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { END_REASON_SIGNOUT } from '@cloudsam/ui';
import hanko from '../hanko-init';
import { setAuthIdentity } from '../utils/explorerApi';
import { idleClock } from '../idle-session';
import { getDbamBase } from '../dbamBase';
import { setUnauthorizedHandler } from '../utils/cloudApi';

const AuthContext = createContext(null);

// The address lives in src/dbamBase.js, because the idle clock needs it and
// this file imports the idle clock — importing it back would be a cycle.

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // True once the idle clock has been started for the session this page
    // load is holding. Reset when a check finds nobody signed in, so a
    // sign-in later in the same page load starts the clock again.
    const sessionStartedRef = useRef(false);

    const checkAuth = useCallback(async () => {
        try {
            setError(null);
            // NOTE: do NOT gate on hanko.session.isValid() — that SDK check
            // reads the SDK's own localStorage, which is per-origin and
            // returns false on any subdomain that hasn't seen the SDK
            // init a session. The authoritative check is whether the cookie
            // rides to /me; just call it and let the response decide.
            const url = `${getDbamBase()}/dbam/v1/me`;
            const response = await fetch(url, { credentials: 'include' });

            if (response.ok) {
                const userData = await response.json();
                const userProfile = userData?.data || userData;
                console.log('[AuthContext] /dbam/v1/me →', {
                    user_id: userProfile?.user_id,
                    email: userProfile?.email,
                    org_id: userProfile?.org_id,
                });
                // Push identity into explorerApi's module-level cache so
                // every subsequent /api/* call carries X-User-ID/X-Org-ID
                // populated from the authenticated user (replaces the old
                // URL-query-param trust path).
                setAuthIdentity({ userId: userProfile?.user_id, orgId: userProfile?.org_id });
                setUser(userProfile);
                // Start the idle clock for this sign-in.
                //
                // This hangs on the session check, not on the Hanko SDK's
                // onSessionCreated event. Both sign-in screens drive the Flow API
                // and call back from its own `success` handler, so that event is not
                // a reliable signal. Relying on it left the end-of-session cookie in
                // place, and every tab opened afterwards locked itself against a
                // session that had just been proved good.
                //
                // It fires on the change from "no session" to "session", so a fresh
                // page load does it once and a repeat check does not touch the clock.
                if (!sessionStartedRef.current) {
                    sessionStartedRef.current = true;
                    idleClock.startSession();
                }
            } else if (response.status === 401 || response.status === 404) {
                sessionStartedRef.current = false;
                // 401 = no/expired session; 404 = authenticated to Hanko but
                // no sam-dbam profile yet. Explorer can't function without a
                // profile, so both cases route to the "open Console" page.
                setUser(null);
            } else {
                console.error('[AuthContext] /me failed:', response.status);
                setUser(null);
            }
        } catch (err) {
            console.error('[AuthContext] checkAuth error:', err);
            setError(err);
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();

        // A session can die while the tab stays open. Hanko's own events only
        // fire when its SDK notices; a 401 from any dbam call is the earlier
        // and more reliable signal, so treat it as the session ending.
        setUnauthorizedHandler(() => setUser(null));

        hanko.onSessionExpired(() => setUser(null));
        hanko.onUserLoggedOut(() => setUser(null));
        // Clear the "this session has ended" cookie and restart the idle clock.
        // Without this, somebody who signs back in is locked out again on the
        // next tick by the cookie left from the sign-out before.
        hanko.onSessionCreated(() => { idleClock.startSession(); checkAuth(); });

        return () => setUnauthorizedHandler(null);
    }, [checkAuth]);

    const logout = useCallback(async () => {
        // Tell the other tabs first, before any network call, so Console,
        // Designer and Channels lock straight away rather than waiting on
        // Hanko. They cannot be closed from here; they watch a shared cookie.
        idleClock.endSession(END_REASON_SIGNOUT);
        // Report the sign-out first, while the session is still valid. Hanko
        // does not tell CloudSAM that a session ended, so without this the
        // stored session expiry stays in the future and the SuperPanel Monitor
        // keeps listing this person as signed in. Never block the sign-out.
        try {
            await fetch(`${getDbamBase()}/dbam/v1/me/signout`, {
                method: 'POST', credentials: 'include',
            });
        } catch (err) {
            console.warn('[AuthContext] sign-out could not be recorded');
        }
        try {
            await hanko.user.logout();
        } catch (err) {
            console.error('[AuthContext] logout error:', err);
        } finally {
            // Clear cached identity so any in-flight /api/* requests don't
            // ship stale headers after sign-out.
            setAuthIdentity({});
            setUser(null);
        }
    }, []);

    const refreshUser = useCallback(async () => { await checkAuth(); }, [checkAuth]);

    return (
        <AuthContext.Provider value={{
            user,
            loading,
            error,
            logout,
            refreshUser,
            isAuthenticated: !!user?.user_id,
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
